// Stripped so a trailing slash in the env var (easy to paste in by accident,
// e.g. from a Railway domain) can't turn `${API_URL}${path}` into `//path`.
export const API_URL = process.env.BUN_PUBLIC_API_URL?.replace(/\/+$/, "");

export type CurrentUser = {
  id: string;
  email: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
};

export type RegisterPayload = {
  email: string;
  password: string;
};

export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = "ApiError";
  }
}

let csrfToken: string | null = null;

// A laptop waking from sleep often regains network a beat after the page's
// first request fires; a bare connection failure here shouldn't be treated
// the same as a real 401 (see fetchCurrentUser / AuthContext mount effect).
const NETWORK_RETRY_DELAYS_MS = [500, 1500];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithRetry(input: string, init?: RequestInit): Promise<Response | null> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetch(input, init);
    } catch {
      const wait = NETWORK_RETRY_DELAYS_MS[attempt];
      if (wait === undefined) return null;
      await delay(wait);
    }
  }
}

// Serializes refresh-session calls across tabs (and within a tab) so two
// concurrent refreshes never present the same single-use refresh_token
// cookie to the backend — that would trip its reuse-detection and revoke
// every session for the user. Falls back to unsynchronized on browsers
// without the Web Locks API.
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && "locks" in navigator) {
    return navigator.locks.request("famibelleza-refresh-session", fn);
  }
  return fn();
}

async function getCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetchWithRetry(`${API_URL}/auth/csrf-token`, { credentials: "include" });
  if (!res || !res.ok) throw new Error("Could not fetch CSRF token");
  const data: { csrf_token: string } = await res.json();
  csrfToken = data.csrf_token;
  return csrfToken;
}

export function readDetailMessage(detail: unknown, fallback: string): string {
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "reason" in detail) {
    return String((detail as { reason: unknown }).reason);
  }
  return fallback;
}

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  if (!SAFE_METHODS.has(method) && !headers.has("X-CSRF-Token")) {
    headers.set("X-CSRF-Token", await getCsrfToken());
  }

  return fetch(`${API_URL}${path}`, { ...init, headers, credentials: "include" });
}

export async function refreshSession(): Promise<boolean> {
  return withRefreshLock(async () => {
    try {
      const token = await getCsrfToken();
      const res = await fetchWithRetry(`${API_URL}/auth/session/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": token },
      });
      if (!res || !res.ok) return false;

      const data = await res.json().catch(() => null);
      if (data?.csrf_token) csrfToken = data.csrf_token;
      return true;
    } catch {
      return false;
    }
  });
}

export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  const res = await fetchWithRetry(`${API_URL}/users/me`, { credentials: "include" });
  if (!res) return null;
  if (res.ok) return res.json();

  // The 5-minute access-token cookie may have expired; the 30-day refresh
  // cookie can still mint a new one before giving up on the session.
  if (res.status === 401 && (await refreshSession())) {
    const retry = await fetchWithRetry(`${API_URL}/users/me`, { credentials: "include" });
    if (retry?.ok) return retry.json();
  }

  return null;
}

export async function checkEmailExists(email: string): Promise<boolean> {
  const res = await fetch(`${API_URL}/auth/email-exists`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiError(
      readDetailMessage(err?.detail, "Could not verify email"),
      res.status
    );
  }
  const data: { exists: boolean } = await res.json();
  return data.exists;
}

export async function login(email: string, password: string): Promise<CurrentUser | null> {
  const token = await getCsrfToken();
  const body = new URLSearchParams({ username: email, password });

  const res = await fetch(`${API_URL}/auth/session/login`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": token },
    body,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new ApiError(
      readDetailMessage(err?.detail, "Invalid email or password"),
      res.status
    );
  }

  const data = await res.json().catch(() => null);
  if (data?.csrf_token) csrfToken = data.csrf_token;

  return fetchCurrentUser();
}

export async function register(payload: RegisterPayload): Promise<void> {
  const token = await getCsrfToken();

  const res = await fetch(`${API_URL}/auth/register`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    if (err?.detail === "REGISTER_USER_ALREADY_EXISTS") {
      throw new Error(
        "An account with this email already exists. Try logging in, or use Continue with Google if you signed up that way."
      );
    }
    throw new Error(readDetailMessage(err?.detail, "Could not create account"));
  }
}

export async function getGoogleAuthorizeUrl(): Promise<string> {
  const res = await fetch(`${API_URL}/auth/google/authorize`, { credentials: "include" });
  if (!res.ok) throw new Error("Could not start Google sign-in");
  const data: { authorization_url: string } = await res.json();
  return data.authorization_url;
}

export async function setProductStatus(
  id: string,
  status: string
): Promise<{ id: string; slug: string; status: { name: string } }> {
  const res = await apiFetch(`/product/${id}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not update product status"));
  }

  return res.json();
}

export async function bulkDeleteProducts(ids: string[]): Promise<void> {
  const res = await apiFetch("/products", {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not delete selected products"));
  }
}

export async function bulkSetProductsStatus(ids: string[], status: string): Promise<void> {
  const res = await apiFetch("/products/status", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, status }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not update selected products"));
  }
}

export type Announcement = {
  id: string;
  message: string;
  message_en: string | null;
  text_color: string | null;
  background_color: string | null;
  // Absent on the copies embedded in products; always set on /announcements.
  position?: number;
};

export type AnnouncementColors = {
  textColor?: string | null;
  backgroundColor?: string | null;
};

export async function listAnnouncements(): Promise<Announcement[]> {
  const res = await apiFetch("/announcements?limit=100");
  if (!res.ok) throw new Error("Could not load announcements");
  const page: { items: Announcement[] } = await res.json();
  return page.items;
}

export async function createAnnouncement(
  message: string,
  messageEn: string | null,
  colors: AnnouncementColors = {}
): Promise<Announcement> {
  const res = await apiFetch("/announcement", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message,
      message_en: messageEn,
      text_color: colors.textColor ?? null,
      background_color: colors.backgroundColor ?? null,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not create announcement"));
  }

  return res.json();
}

export async function updateAnnouncement(
  id: string,
  message: string,
  messageEn: string | null,
  colors: AnnouncementColors = {}
): Promise<Announcement> {
  const res = await apiFetch(`/announcement/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message,
      message_en: messageEn,
      text_color: colors.textColor ?? null,
      background_color: colors.backgroundColor ?? null,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not update announcement"));
  }

  return res.json();
}

export async function reorderAnnouncements(ids: string[]): Promise<void> {
  const res = await apiFetch("/announcements/order", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not reorder announcements"));
  }
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const res = await apiFetch(`/announcement/${id}`, { method: "DELETE" });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not delete announcement"));
  }
}

export async function bulkSetVariantsAnnouncement(
  ids: number[],
  announcementId: string | null,
  clearIds: number[] = []
): Promise<void> {
  const res = await apiFetch("/variants/announcement", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ids,
      announcement_id: announcementId,
      clear_ids: clearIds,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      readDetailMessage(body?.detail, "Could not update selected variants' announcement")
    );
  }
}

export async function bulkSetVariantsUnavailable(
  ids: number[],
  isUnavailable: boolean
): Promise<void> {
  const res = await apiFetch("/variants/unavailable", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, is_unavailable: isUnavailable }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(
      readDetailMessage(body?.detail, "Could not update selected variants' availability")
    );
  }
}

export async function uploadVariantImage(variantId: number, file: File): Promise<string | null> {
  const formData = new FormData();
  formData.append("file", file);

  // No Content-Type here on purpose: the browser sets multipart/form-data
  // with the correct boundary itself when the body is a FormData.
  const res = await apiFetch(`/image/${variantId}`, {
    method: "POST",
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(readDetailMessage(body?.detail, "Could not upload image"));
  }

  const data: { url: string | null } = await res.json();
  return data.url;
}

export async function checkSkuAvailability(
  sku: string,
  excludeId: number | undefined,
  signal: AbortSignal
): Promise<boolean> {
  const params = new URLSearchParams({ sku });
  if (excludeId !== undefined) params.set("exclude_id", String(excludeId));

  const res = await apiFetch(`/variant/sku-availability?${params}`, { signal });
  if (!res.ok) throw new Error("Could not check SKU availability");
  const data: { available: boolean } = await res.json();
  return data.available;
}

export async function getVariantImage(variantId: number): Promise<string | null> {
  const res = await apiFetch(`/image/${variantId}`);
  if (!res.ok) return null;
  const data: { url: string | null } = await res.json();
  return data.url;
}

export async function getVariantImages(variantIds: number[]): Promise<Record<number, string>> {
  const ids = [...new Set(variantIds)];
  if (!ids.length) return {};

  const res = await apiFetch(`/images?variant_ids=${ids.join(",")}`);
  if (!res.ok) return {};
  return res.json();
}

export async function requestVerifyEmail(email: string): Promise<void> {
  const token = await getCsrfToken();
  await fetch(`${API_URL}/auth/request-verify-token`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body: JSON.stringify({ email }),
  });
  // Always resolves silently: fastapi-users returns 202 regardless of
  // whether the email exists or is already verified, to avoid leaking
  // account state to the caller.
}

export async function verifyEmail(token: string): Promise<void> {
  const csrfToken = await getCsrfToken();
  const res = await fetch(`${API_URL}/auth/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(readDetailMessage(err?.detail, "This verification link is invalid or has expired"));
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  const token = await getCsrfToken();
  await fetch(`${API_URL}/auth/forgot-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
    body: JSON.stringify({ email }),
  });
  // Always resolves silently, same reasoning as requestVerifyEmail.
}

export async function resetPassword(token: string, password: string): Promise<void> {
  const csrfToken = await getCsrfToken();
  const res = await fetch(`${API_URL}/auth/reset-password`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ token, password }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(readDetailMessage(err?.detail, "Could not reset password"));
  }
}

export async function logout(): Promise<void> {
  const token = await getCsrfToken();
  await fetch(`${API_URL}/auth/session/logout`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": token },
  });
  csrfToken = null;
}
